import { useMemo } from 'react'

import { CustomizableDashboard, ModalContext } from '@domain-group/suit-theme'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { handleCallbackAction } from '@domain-group/javascript-utils'
import type { HandleCallbackActionParams } from '@domain-group/javascript-utils'

import type { PageContextPayload } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useUser from '@hooks/useUser'
import type { CampaignParams } from '@hooks/useAgencyMarketing'

import CreateAgencyMarketingCampaignModal from '@modals/CreateAgencyMarketingCampaignModal'
import CreateCampaignModal from '@modals/CreateCampaignModal'
import ImportCampaignModal from '@modals/ImportCampaignModal'
import NewCampaignModal from '@modals/NewCampaignModal'

import type { ActivityModel, CampaignModel } from '@models/types'

import Content from '../DashboardModules/Content'
import ContentSidebar from '../DashboardModules/ContentSidebar'
import PageLayout from '../PageLayout'

import { dashboardDefaultLayout, generateAvailableModules } from '../../utils/customizableDashboard'

const defaultState = {
  editingDashboard: false,
  showCreateAgencyMarketingCampaignModal: false,
  showCreateCampaignModal: false,
  showImportCampaignModal: false,
  showManageActivityModal: false,
  showNewCampaignModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void
type UseUserPayload = ReturnType<typeof useUser>

const callbacks = (
  // eslint-disable-next-line no-undef
  component: keyof typeof componentCallbacks,
  state: typeof defaultState,
  setState: SetState,
  userCallbacks?: UseUserPayload['callbacks'],
) => {
  const { editingDashboard } = state
  const { saveUserDashboard } = userCallbacks || {}

  const componentCallbacks = {
    CreateAgencyMarketingCampaignModal: {
      closeModal: () => setState({ showCreateAgencyMarketingCampaignModal: false }),
      createArtworkWithCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateCampaignModal: {
      closeModal: () => setState({ showCreateCampaignModal: false }),
      createCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CustomizableDashboard: {
      // @ts-expect-error TS2722
      saveDashboard: (dashboardKey: string, activeLayout: object) => saveUserDashboard(dashboardKey, activeLayout),
      toggleEditDashboard: () => setState({ editingDashboard: !editingDashboard }),
    },
    ImportCampaignModal: {
      closeModal: () => setState({ showImportCampaignModal: false }),
      importCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    NewCampaignModal: {
      closeModal: () => setState({ showNewCampaignModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const PageContent = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    editingDashboard,
    showCreateAgencyMarketingCampaignModal,
    showCreateCampaignModal,
    showImportCampaignModal,
    showNewCampaignModal,
  } = state

  const currentUserPayload = useCurrentUser()
  const { currentUser } = currentUserPayload
  const { callbacks: userCallbacks, user } = useUser(currentUser)

  const customizedLayout = user?.options?.custom_dashboards?.agency_dashboard

  const availableModules = useMemo(() => generateAvailableModules(currentUserPayload), [user.id])

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateAgencyMarketingCampaignModal: (payload: {
        agencyMarketingCampaignParams: CampaignParams,
      }) => {
        setModalData('CreateAgencyMarketingCampaignModal', payload)
        setState({ showCreateAgencyMarketingCampaignModal: true })
      },
      showCreateCampaignModal: (payload: { campaign: CampaignModel }) => {
        setModalData('CreateCampaignModal', payload)
        setState({ showCreateCampaignModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showImportCampaignModal: (payload: { campaign: CampaignModel }) => {
        setModalData('ImportCampaignModal', payload)
        setState({ showImportCampaignModal: true })
      },
      showNewCampaignModal: (payload: { campaign: CampaignModel }) => {
        setModalData('NewCampaignModal', payload)
        setState({ showNewCampaignModal: true })
      },
    },
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageLayout activeTabBarItemKey="overview">
          <CustomizableDashboard
            // @ts-expect-error TS2322
            availableModules={availableModules}
            callbacks={callbacks('CustomizableDashboard', state, setState, userCallbacks)}
            columns={[
              {
                id: 'main',
                component: <Content />,
                title: 'Main',
              },
              {
                id: 'sidebar',
                component: (
                  // @ts-expect-error TS2739
                  <ContentSidebar
                    callbacks={{ customizeDashboard: () => setState({ editingDashboard: !editingDashboard }) }}
                  />
                ),
                title: 'Sidebar',
              },
            ]}
            customizedLayout={customizedLayout}
            dashboardKey="agency_dashboard"
            dashboardReady={!!user.id}
            defaultLayout={dashboardDefaultLayout()}
            editing={editingDashboard}
          />

          <CreateAgencyMarketingCampaignModal
          // @ts-expect-error TS2739
            callbacks={callbacks('CreateAgencyMarketingCampaignModal', state, setState)}
            showModal={showCreateAgencyMarketingCampaignModal}
          />

          <CreateCampaignModal
            // @ts-expect-error TS2322
            callbacks={callbacks('CreateCampaignModal', state, setState, userCallbacks)}
            showModal={showCreateCampaignModal}
          />

          <ImportCampaignModal
            // @ts-expect-error TS2322
            callbacks={callbacks('ImportCampaignModal', state, setState)}
            showModal={showImportCampaignModal}
          />

          <NewCampaignModal
            // @ts-expect-error TS2322
            callbacks={callbacks('NewCampaignModal', state, setState)}
            showModal={showNewCampaignModal}
          />
        </PageLayout>
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default PageContent
